import {
  clamp,
  interpolate,
  number_or_default,
  progress_from_data,
  source_text_from_data,
} from "./banana_progress_gauge_core_base.js";
import { normalise_trace_points } from "./banana_progress_gauge_core_trace.js";

const BANANA_COLOR_LAYERS = [
  {id : "bodyBase", label : "Corps principal", start : "#45b95c", end : "#ffe400"},
  {id : "bodyLight", label : "Reflet principal", start : "#9be476", end : "#ffed6d"},
  {id : "bodyShadow", label : "Ombre principale", start : "#229246", end : "#efd40c"},
  {id : "tip", label : "Pointe droite", start : "#995515", end : "#995515"},
  {id : "stemBase", label : "Tige gauche", start : "#995515", end : "#995515"},
  {id : "stemDark", label : "Tige sombre", start : "#784111", end : "#784111"},
  {id : "stemLight", label : "Tige claire", start : "#bc711a", end : "#bc711a"},
  {id : "shine", label : "Reflets blancs", start : "#ffffff", end : "#ffffff"},
];

function default_layer_colors ()
  {
    return default_layer_colors_for_definitions (BANANA_COLOR_LAYERS);
  }

function default_layer_colors_for_definitions (pDefinitions)
  {
    const tColors = {};
    (Array.isArray (pDefinitions) && pDefinitions.length ? pDefinitions : BANANA_COLOR_LAYERS).forEach ((pLayer) =>
      {
        tColors[pLayer.id] = {start : pLayer.start, end : pLayer.end};
      });
    return tColors;
  }

function layer_definition (pLayerId)
  {
    return BANANA_COLOR_LAYERS.find ((pLayer) => pLayer.id === pLayerId)
      || BANANA_COLOR_LAYERS[0];
  }

export function normalise_hex_color (pValue, pFallback)
  {
    const tValue = String (pValue || "").trim ();
    return /^#[0-9a-f]{6}$/i.test (tValue) ? tValue : pFallback;
  }

function valid_hex_color_or_null (pValue)
  {
    const tValue = String (pValue || "").trim ();
    return /^#[0-9a-f]{6}$/i.test (tValue) ? tValue : null;
  }

function color_to_hex_or_null (pValue)
  {
    const tValue = String (pValue || "").trim ().toLowerCase ();
    if (/^#[0-9a-f]{6}$/.test (tValue)) return tValue;
    if (/^#[0-9a-f]{3}$/.test (tValue))
      return "#" + tValue.slice (1).split ("").map ((pCar) => pCar + pCar).join ("");
    const tRgb = tValue.match (/^rgba?\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*[0-9.]+)?\s*\)$/);
    if (!tRgb) return null;
    return rgb_to_hex ({
      r : Math.round (clamp (number_or_default (tRgb[1], 0), 0, 255)),
      g : Math.round (clamp (number_or_default (tRgb[2], 0), 0, 255)),
      b : Math.round (clamp (number_or_default (tRgb[3], 0), 0, 255)),
    });
  }

function normalise_layer_colors (pColors)
  {
    return normalise_layer_colors_for_definitions (pColors, BANANA_COLOR_LAYERS);
  }

export function normalise_layer_colors_for_definitions (pColors, pDefinitions)
  {
    const tDefinitions = Array.isArray (pDefinitions) && pDefinitions.length ? pDefinitions : BANANA_COLOR_LAYERS;
    const tDefaults = default_layer_colors_for_definitions (tDefinitions);
    const tSource = pColors && typeof pColors === "object" ? pColors : {};
    const tColors = {};
    tDefinitions.forEach ((pLayer) =>
      {
        const tSourceLayer = tSource[pLayer.id] || {};
        tColors[pLayer.id] = {
          start : normalise_hex_color (tSourceLayer.start, tDefaults[pLayer.id].start),
          end   : normalise_hex_color (tSourceLayer.end, tDefaults[pLayer.id].end),
        };
      });
    Object.keys (tSource).forEach ((pLayerId) =>
      {
        if (tColors[pLayerId]) return;
        const tSourceLayer = tSource[pLayerId] && typeof tSource[pLayerId] === "object"
          ? tSource[pLayerId]
          : {};
        const tStart = valid_hex_color_or_null (tSourceLayer.start);
        const tEnd = valid_hex_color_or_null (tSourceLayer.end);
        if (tStart && tEnd) tColors[pLayerId] = {start : tStart, end : tEnd};
      });
    return tColors;
  }

export function normalise_banana_progress_gauge_config (pValue)
  {
    const tSource = pValue && typeof pValue === "object" ? pValue : {};
    return {
      tracePoints : normalise_trace_points (tSource.tracePoints),
      layerColors : normalise_layer_colors (tSource.layerColors),
    };
  }

export function layer_colors_from_data (pData)
  {
    return normalise_layer_colors_for_definitions (
      (pData && pData.layerColors)
      || (pData && pData.config && pData.config.layerColors),
      color_layer_definitions_from_data (pData)
    );
  }

export function layer_style_from_data (pData, pLayerId, pPhase)
  {
    const tLayer = layer_definition (pLayerId);
    const tColors = layer_colors_from_data (pData);
    const tLayerColors = tColors[tLayer.id] || {start : tLayer.start, end : tLayer.end};
    const tColor = pPhase === "end"
      ? tLayerColors.end
      : (pPhase === "progress"
        ? interpolate_hex_color (tLayerColors.start, tLayerColors.end, progress_from_data (pData))
        : tLayerColors.start);
    return `fill: ${tColor};`;
  }

function interpolate_hex_color (pStart, pEnd, pProgress)
  {
    const tProgress = clamp (number_or_default (pProgress, 0), 0, 1);
    const tStart = hex_to_rgb (pStart);
    const tEnd = hex_to_rgb (pEnd);
    return rgb_to_hex ({
      r : Math.round (interpolate (tStart.r, tEnd.r, tProgress)),
      g : Math.round (interpolate (tStart.g, tEnd.g, tProgress)),
      b : Math.round (interpolate (tStart.b, tEnd.b, tProgress)),
    });
  }

function hex_to_rgb (pColor)
  {
    const tColor = normalise_hex_color (pColor, "#000000").slice (1);
    return {
      r : parseInt (tColor.slice (0, 2), 16),
      g : parseInt (tColor.slice (2, 4), 16),
      b : parseInt (tColor.slice (4, 6), 16),
    };
  }

function rgb_to_hex (pRgb)
  {
    return "#" + [pRgb.r, pRgb.g, pRgb.b]
      .map ((pValue) => clamp (pValue, 0, 255).toString (16).padStart (2, "0"))
      .join ("");
  }

export function selected_layer_class_from_data (pData, pLayerId)
  {
    return pData && pData.selectedColorLayer === pLayerId ? "is-color-selected" : "";
  }

export function svg_layer_id_from_name (pName, pIndex)
  {
    const tBase = String (pName || "").trim ()
      .replace (/[^a-z0-9_-]+/gi, "_")
      .replace (/^_+|_+$/g, "");
    return `svg_${tBase || (pIndex + 1)}`;
  }

function svg_layer_label_from_id (pLayerId)
  {
    return String (pLayerId || "")
      .replace (/^svg_/, "")
      .replace (/[_-]+/g, " ")
      .trim ()
      || "Calque SVG";
  }

export function svg_layer_name_from_node (pNode, pIndex)
  {
    if (!pNode || typeof pNode.getAttribute !== "function") return `svg_${pIndex + 1}`;
    return pNode.getAttribute ("id")
      || pNode.getAttribute ("data-name")
      || pNode.getAttribute ("name")
      || pNode.getAttribute ("inkscape:label")
      || pNode.getAttribute ("sodipodi:label")
      || `${(pNode.tagName || "svg").toLowerCase ()}_${pIndex + 1}`;
  }

function svg_style_declaration_value (pStyle, pProperty)
  {
    const tStyle = String (pStyle || "");
    const tProperty = String (pProperty || "").replace (/[.*+?^${}()|[\]\\]/g, "\\$&");
    const tMatch = tStyle.match (new RegExp (`(?:^|;)\\s*${tProperty}\\s*:\\s*([^;]+)`, "i"));
    return tMatch ? tMatch[1].trim () : "";
  }

function source_svg_css_fill_rules (pRoot)
  {
    const tRules = new Map ();
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return tRules;
    Array.from (pRoot.querySelectorAll ("style")).forEach ((pStyle) =>
      {
        const tText = pStyle.textContent || "";
        const tRegex = /([^{}]+)\{([^{}]+)\}/g;
        let tMatch;
        while ((tMatch = tRegex.exec (tText)))
          {
            const tFill = color_to_hex_or_null (svg_style_declaration_value (tMatch[2], "fill"));
            if (!tFill) continue;
            tMatch[1].split (",").forEach ((pSelector) =>
              {
                const tSelector = pSelector.trim ();
                if (/^[.#][a-z0-9_-]+$/i.test (tSelector)) tRules.set (tSelector, tFill);
              });
          }
      });
    return tRules;
  }

function source_svg_css_fill_for_node (pNode, pRules)
  {
    if (!pNode || !pRules || !pRules.size || typeof pNode.getAttribute !== "function") return null;
    const tId = pNode.getAttribute ("id");
    if (tId && pRules.has (`#${tId}`)) return pRules.get (`#${tId}`);
    const tClasses = String (pNode.getAttribute ("class") || "").split (/\s+/).filter (Boolean);
    for (let i = 0; i < tClasses.length; i += 1)
      {
        const tColor = pRules.get (`.${tClasses[i]}`);
        if (tColor) return tColor;
      }
    return null;
  }

function source_svg_original_fill_for_node (pNode, pRules)
  {
    if (!pNode || typeof pNode.getAttribute !== "function") return null;
    const tStyleColor = color_to_hex_or_null (svg_style_declaration_value (pNode.getAttribute ("style"), "fill"));
    if (tStyleColor) return tStyleColor;
    const tCssColor = source_svg_css_fill_for_node (pNode, pRules);
    if (tCssColor) return tCssColor;
    const tAttrColor = color_to_hex_or_null (pNode.getAttribute ("fill"));
    if (tAttrColor) return tAttrColor;
    const tParent = pNode.parentNode && pNode.parentNode.nodeType === 1 ? pNode.parentNode : null;
    return tParent ? source_svg_original_fill_for_node (tParent, pRules) : null;
  }

function color_layers_from_source_svg (pText)
  {
    if (!pText || typeof DOMParser === "undefined") return [];
    try
      {
        const tDoc = new DOMParser ().parseFromString (pText, "image/svg+xml");
        const tRoot = tDoc && tDoc.documentElement;
        const tErreur = tDoc && typeof tDoc.querySelector === "function"
          ? tDoc.querySelector ("parsererror")
          : null;
        if (!tRoot || tRoot.nodeName.toLowerCase () !== "svg" || tErreur) return [];
        const tUsedIds = new Set ();
        const tCssFillRules = source_svg_css_fill_rules (tRoot);
        return Array.from (tRoot.querySelectorAll ("path, polygon, polyline, rect, circle, ellipse, line, text, use"))
          .map ((pNode, pIndex) =>
            {
              const tBaseId = svg_layer_id_from_name (svg_layer_name_from_node (pNode, pIndex), pIndex);
              let tId = tBaseId;
              let tSuffix = 2;
              while (tUsedIds.has (tId))
                {
                  tId = `${tBaseId}_${tSuffix}`;
                  tSuffix += 1;
                }
              tUsedIds.add (tId);
              return {
                id : tId,
                label : svg_layer_label_from_id (tId),
                start : "#45b95c",
                end : source_svg_original_fill_for_node (pNode, tCssFillRules) || "#ffe400",
              };
            });
      }
    catch (e) { return []; }
  }

export function color_layer_definitions_from_data (pData)
  {
    const tSourceText = source_text_from_data (pData);
    const tSourceLayers = color_layers_from_source_svg (tSourceText);
    return tSourceText && tSourceLayers.length
      ? tSourceLayers
      : BANANA_COLOR_LAYERS.slice ();
  }

